import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ContractedCompany } from 'src/models/contracted-company';

export interface ConsortiumMember {
  id?: string;
  contractId?: string;
  consortiumId?: string;
  contractedCompanyId: string;
  isLeader: boolean;
  participationPercentage?: number;
  contractedCompany?: ContractedCompany;
  softDelete?: boolean;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface ConsortiumMemberPayload {
  contractedCompanyId: string;
  isLeader: boolean;
  participationPercentage?: number;
}

export interface SetupConsortiumPayload {
  name?: string;
  members: ConsortiumMemberPayload[];
}

export interface Consortium {
  id: string;
  workspaceId: string;
  contractId?: string;
  name: string;
  members?: ConsortiumMember[];
  softDelete?: boolean;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface ConsortiumDetails {
  consortium?: Consortium | null;
  isConsortium: boolean;
  leader?: ContractedCompany;
  leaderMembership?: ConsortiumMember;
  members: ConsortiumMember[];
  totalParticipation: number;
  companiesCount: number;
}

@Injectable({
  providedIn: 'root',
})
export class ContractConsortiumService {
  private readonly baseUrl = environment.endpoint();

  constructor(private http: HttpClient) {}

  /**
   * Lista membros do consórcio de um contrato
   */
  listMembers(contractId: string): Observable<ConsortiumMember[]> {
    return this.http.get<ConsortiumMember[]>(
      `${this.baseUrl}/contracts/${contractId}/consortium/members`
    );
  }

  /**
   * Adiciona um membro ao consórcio
   */
  addMember(contractId: string, payload: ConsortiumMemberPayload): Observable<ConsortiumMember> {
    return this.http.post<ConsortiumMember>(
      `${this.baseUrl}/contracts/${contractId}/consortium/members`,
      payload
    );
  }

  /**
   * Atualiza um membro do consórcio
   */
  updateMember(
    contractId: string,
    memberId: string,
    payload: Partial<ConsortiumMemberPayload>
  ): Observable<ConsortiumMember> {
    return this.http.patch<ConsortiumMember>(
      `${this.baseUrl}/contracts/${contractId}/consortium/members/${memberId}`,
      payload
    );
  }

  /**
   * Remove um membro do consórcio
   */
  removeMember(contractId: string, memberId: string): Observable<ConsortiumMember> {
    return this.http.delete<ConsortiumMember>(
      `${this.baseUrl}/contracts/${contractId}/consortium/members/${memberId}`
    );
  }

  /**
   * Configura o consórcio completo (substitui todos os membros)
   */
  setupConsortium(contractId: string, payload: SetupConsortiumPayload): Observable<ConsortiumMember[]> {
    return this.http.post<ConsortiumMember[]>(
      `${this.baseUrl}/contracts/${contractId}/consortium`,
      payload
    );
  }

  /**
   * Obtém detalhes do consórcio
   */
  getConsortiumDetails(contractId: string): Observable<ConsortiumDetails> {
    return this.http.get<ConsortiumDetails>(
      `${this.baseUrl}/contracts/${contractId}/consortium`
    );
  }

  /**
   * Atualiza nome do consórcio
   */
  renameConsortium(contractId: string, name: string): Observable<Consortium> {
    return this.http.patch<Consortium>(
      `${this.baseUrl}/contracts/${contractId}/consortium`,
      { name }
    );
  }

  /**
   * Lista consórcios do workspace
   */
  listConsortiums(): Observable<Consortium[]> {
    return this.http.get<Consortium[]>(
      `${this.baseUrl}/contracted-companies/consortiums`
    );
  }

  createConsortium(name: string): Observable<Consortium> {
    return this.http.post<Consortium>(
      `${this.baseUrl}/contracted-companies/consortiums`,
      { name }
    );
  }

  updateConsortium(consortiumId: string, name: string): Observable<Consortium> {
    return this.http.patch<Consortium>(
      `${this.baseUrl}/contracted-companies/consortiums/${consortiumId}`,
      { name }
    );
  }

  deleteConsortium(consortiumId: string): Observable<Consortium> {
    return this.http.delete<Consortium>(
      `${this.baseUrl}/contracted-companies/consortiums/${consortiumId}`
    );
  }

  listConsortiumMembersById(consortiumId: string): Observable<ConsortiumMember[]> {
    return this.http.get<ConsortiumMember[]>(
      `${this.baseUrl}/contracted-companies/consortiums/${consortiumId}/members`
    );
  }

  addConsortiumMember(consortiumId: string, payload: ConsortiumMemberPayload): Observable<ConsortiumMember> {
    return this.http.post<ConsortiumMember>(
      `${this.baseUrl}/contracted-companies/consortiums/${consortiumId}/members`,
      payload
    );
  }

  updateConsortiumMember(
    consortiumId: string,
    memberId: string,
    payload: Partial<ConsortiumMemberPayload>
  ): Observable<ConsortiumMember> {
    return this.http.patch<ConsortiumMember>(
      `${this.baseUrl}/contracted-companies/consortiums/${consortiumId}/members/${memberId}`,
      payload
    );
  }

  removeConsortiumMember(consortiumId: string, memberId: string): Observable<ConsortiumMember> {
    return this.http.delete<ConsortiumMember>(
      `${this.baseUrl}/contracted-companies/consortiums/${consortiumId}/members/${memberId}`
    );
  }
}
